import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { 
  CalendarIcon, 
  PlusIcon, 
  MapPinIcon,
  ClockIcon,
  UserIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  UserGroupIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  location: string;
  category: 'COMMUNITY' | 'MAINTENANCE' | 'SOCIAL' | 'MEETING';
  organizer?: string;
  hoaId: string;
  rsvpCount?: number;
  hasRSVPed?: boolean;
}

interface RSVP {
  id: string;
  eventId: string;
  userId: string;
  userName: string;
  status: 'ATTENDING' | 'NOT_ATTENDING' | 'MAYBE';
}

const EVENT_ICONS = {
  COMMUNITY: '🏘️',
  MAINTENANCE: '🔧',
  SOCIAL: '🎉',
  MEETING: '📋'
};

// Convert a stored ISO timestamp (or any Date-parseable string) into the
// "YYYY-MM-DDTHH:MM" shape required by <input type="datetime-local">. The
// browser displays this in the user's local timezone, matching how the rest
// of the UI renders event dates.
const toLocalInputValue = (value: string | undefined | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Convert a datetime-local input value ("YYYY-MM-DDTHH:MM", no timezone)
// into a proper UTC ISO string. JS's Date constructor interprets that
// format as the user's LOCAL time, which is what the picker shows. Sending
// the raw string instead lets the server parse it as UTC and shift the
// time by the user's offset.
const localInputToISO = (value: string): string => {
  if (!value) return value;
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toISOString();
};

const EventCategories = [
  { value: 'COMMUNITY', label: 'Community Event', color: 'text-blue-400 bg-blue-500/20' },
  { value: 'MAINTENANCE', label: 'Maintenance', color: 'text-yellow-400 bg-yellow-500/20' },
  { value: 'SOCIAL', label: 'Social Gathering', color: 'text-green-400 bg-green-500/20' },
  { value: 'MEETING', label: 'Owner / HOA Meeting', color: 'text-cyan-300 bg-cyan-400/20' }
];

const Events: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [newEvent, setNewEvent] = useState<Omit<Event, 'id' | 'hoaId'>>({ 
    title: '', 
    description: '', 
    date: '', 
    location: '', 
    category: 'COMMUNITY' 
  });
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const { user } = useAuth();

  const fetchEvents = useCallback(async () => {
    if (!user) return;

    try {
      // api injects Authorization and (for admins) the x-selected-group header
      // so the backend can resolve the currently-active property group instead
      // of the JWT's primary one.
      const eventsResponse = await api.get<Event[]>('/api/events');

      // Fetch RSVPs for the current user
      let userRsvps: RSVP[] = [];
      try {
        const rsvpResponse = await api.get<RSVP[]>('/api/events/rsvps', {
          params: { userId: user.id }
        });
        userRsvps = rsvpResponse.data;
      } catch {
        // RSVPs endpoint may not exist yet
      }
      
      // Merge RSVP status into events
      const eventsWithRSVP = eventsResponse.data.map(event => ({
        ...event,
        hasRSVPed: userRsvps.some(rsvp => rsvp.eventId === event.id),
        rsvpCount: event.rsvpCount || 0
      }));
      
      // Sort events by date, upcoming events first
      const sortedEvents = eventsWithRSVP.sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      
      setEvents(sortedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      // No mock data - show empty state
      setEvents([]);
    }
  }, [user]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      await api.post('/api/events', {
        ...newEvent,
        date: localInputToISO(newEvent.date),
        organizer: user.name
      });

      fetchEvents();
      setNewEvent({
        title: '',
        description: '',
        date: '',
        location: '',
        category: 'COMMUNITY'
      });
      setShowEventModal(false);
    } catch (error: any) {
      console.error('Error creating event:', error);
      const msg = error?.response?.data?.message || 'Could not create event. Please try again.';
      alert(`⚠️ ${msg}`);
    }
  };

  const handleUpdateEvent = async (eventId: string) => {
    try {
      await api.put(`/api/events/${eventId}`, {
        ...newEvent,
        date: localInputToISO(newEvent.date),
      });
      fetchEvents();
      setIsEditing(null);
      setShowEventModal(false);
      setNewEvent({ title: '', description: '', date: '', location: '', category: 'COMMUNITY' });
    } catch (error: any) {
      console.error('Error updating event:', error);
      const msg = error?.response?.data?.message || 'Could not update event. Please try again.';
      alert(`⚠️ ${msg}`);
    }
  };

  const handleDeleteEvent = async (eventId: string, eventTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete "${eventTitle}"?`)) return;

    try {
      await api.delete(`/api/events/${eventId}`);
      fetchEvents();
    } catch (error: any) {
      console.error('Error deleting event:', error);
      const msg = error?.response?.data?.message || `Could not delete "${eventTitle}".`;
      alert(`⚠️ ${msg}`);
    }
  };

  const handleRSVP = async (eventId: string, eventTitle: string) => {
    if (!user?.id) return;

    try {
      await api.post('/api/events/rsvp', {
        eventId,
        userId: user.id,
        status: 'ATTENDING'
      });

      fetchEvents();
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      // RSVP endpoint may not exist yet — optimistically update local state.
      setEvents(events.map(event =>
        event.id === eventId
          ? { ...event, hasRSVPed: true, rsvpCount: (event.rsvpCount || 0) + 1 }
          : event
      ));
      alert(`🎉 RSVP confirmed for "${eventTitle}"!`);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
      full: date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      })
    };
  };

  const getCategoryColor = (category: string) => {
    return EventCategories.find(cat => cat.value === category)?.color || 'text-gray-400 bg-gray-500/20';
  };

  const filteredEvents = selectedCategory === 'all' 
    ? events 
    : events.filter(event => event.category === selectedCategory);

  const upcomingEvents = events.filter(event => new Date(event.date) > new Date()).slice(0, 3);
  const isAdmin = user?.role === 'ADMIN';

  return (
    <div className="space-y-8">
      {/* Calendar subscription panel — lets admins, residents, and vendors
          subscribe to this property group's events in Google / Outlook / Apple */}
      <CalendarSubscribePanel />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Community Events</h1>
          <p className="text-white/60">
            Stay connected with your community and upcoming activities
          </p>
        </div>
        {isAdmin && (
          <button 
            onClick={() => {
              setIsEditing(null);
              setNewEvent({ title: '', description: '', date: '', location: '', category: 'COMMUNITY' });
              setShowEventModal(true);
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Create Event</span>
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Upcoming Events</p>
              <p className="text-2xl font-bold text-[#22d3ee] mt-1">{upcomingEvents.length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center">
              <CalendarIcon className="w-6 h-6 text-[#22d3ee]" />
            </div>
          </div>
        </div>

        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">My RSVPs</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {events.filter(e => e.hasRSVPed).length}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 flex items-center justify-center">
              <CheckIcon className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Total Events</p>
              <p className="text-2xl font-bold text-blue-400 mt-1">{events.length}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500/20 to-cyan-400/20 flex items-center justify-center">
              <CalendarIcon className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Avg Attendance</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">
                {Math.round(events.reduce((sum, e) => sum + (e.rsvpCount || 0), 0) / events.length) || 0}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 flex items-center justify-center">
              <UserGroupIcon className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Category Filters */}
      <div className="floating-card">
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`
              px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200
              ${selectedCategory === 'all' 
                ? 'bg-[#22d3ee] text-white shadow-lg shadow-[#22d3ee]/25' 
                : 'bg-white/10 text-white/80 hover:bg-white/20'}
            `}
          >
            All Events ({events.length})
          </button>
          {EventCategories.map(category => (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`
                px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2
                ${selectedCategory === category.value 
                  ? 'bg-[#22d3ee] text-white shadow-lg shadow-[#22d3ee]/25' 
                  : 'bg-white/10 text-white/80 hover:bg-white/20'}
              `}
            >
              <span>{EVENT_ICONS[category.value as keyof typeof EVENT_ICONS]}</span>
              <span>{category.label} ({events.filter(e => e.category === category.value).length})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Events Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEvents.map(event => {
          const dateInfo = formatDate(event.date);
          const isUpcoming = new Date(event.date) > new Date();
          
          return (
            <div 
              key={event.id} 
              className="floating-card group hover:scale-[1.02] transition-all duration-200"
            >
              {/* Event Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">{EVENT_ICONS[event.category]}</div>
                  <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getCategoryColor(event.category)}`}>
                    {event.category}
                  </span>
                </div>
                {isAdmin && (
                  <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => {
                        setIsEditing(event.id);
                        setNewEvent({
                          title: event.title,
                          description: event.description,
                          // datetime-local needs "YYYY-MM-DDTHH:MM" (no
                          // seconds, no "Z"); the stored ISO timestamp is
                          // rejected by the input and would leave it blank.
                          date: toLocalInputValue(event.date),
                          location: event.location,
                          category: event.category
                        });
                        setShowEventModal(true);
                      }}
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteEvent(event.id, event.title)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Event Title & Description */}
              <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#22d3ee] transition-colors">
                {event.title}
              </h3>
              <p className="text-white/60 text-sm mb-4 line-clamp-2">
                {event.description}
              </p>

              {/* Event Details */}
              <div className="space-y-2 text-xs text-white/40 mb-4">
                <div className="flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  <span>{dateInfo.full}</span>
                </div>
                <div className="flex items-center">
                  <ClockIcon className="w-4 h-4 mr-2" />
                  <span>{dateInfo.time}</span>
                </div>
                <div className="flex items-center">
                  <MapPinIcon className="w-4 h-4 mr-2" />
                  <span>{event.location}</span>
                </div>
                {event.organizer && (
                  <div className="flex items-center">
                    <UserIcon className="w-4 h-4 mr-2" />
                    <span>Organized by {event.organizer}</span>
                  </div>
                )}
              </div>

              {/* RSVP Section */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div className="flex items-center space-x-2 text-xs text-white/60">
                  <UserGroupIcon className="w-4 h-4" />
                  <span>{event.rsvpCount || 0} attending</span>
                </div>
                
                {isUpcoming && (
                  event.hasRSVPed ? (
                    <div className="flex items-center space-x-2 text-xs text-green-400">
                      <CheckIcon className="w-4 h-4" />
                      <span>You're attending</span>
                    </div>
                  ) : (
                    <button 
                      onClick={() => handleRSVP(event.id, event.title)}
                      className="btn-secondary text-xs px-3 py-1"
                    >
                      RSVP
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredEvents.length === 0 && (
        <div className="floating-card text-center py-12">
          <CalendarIcon className="w-16 h-16 text-white/20 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white/60 mb-2">No events found</h3>
          <p className="text-white/40">
            {selectedCategory === 'all' 
              ? 'No events scheduled yet' 
              : `No ${selectedCategory.toLowerCase()} events scheduled`}
          </p>
        </div>
      )}

      {/* Event Creation/Edit Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {isEditing ? 'Edit Event' : 'Create New Event'}
              </h2>
              <button 
                onClick={() => setShowEventModal(false)}
                className="text-white/60 hover:text-white transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                isEditing ? handleUpdateEvent(isEditing) : handleCreateEvent(e);
              }} 
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">Event Title</label>
                  <input
                    type="text"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                    className="input-field w-full"
                    placeholder="Enter event title"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">Category</label>
                  <select
                    value={newEvent.category}
                    onChange={(e) => setNewEvent({
                      ...newEvent, 
                      category: e.target.value as Event['category']
                    })}
                    className="input-field w-full"
                  >
                    {EventCategories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">Description</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  className="input-field w-full h-20 resize-none"
                  placeholder="Event description..."
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">Date and Time</label>
                  <input
                    type="datetime-local"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                    className="input-field w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">Location</label>
                  <input
                    type="text"
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                    className="input-field w-full"
                    placeholder="Event location"
                    required
                  />
                </div>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEventModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {isEditing ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;

// ─────────────────────────────────────────────────────────────────────────────
// Calendar Subscribe Panel
//
// Renders inside the Events page for admins, residents, and vendors. Lists
// the property groups the caller can see and provides a per-group iCal
// subscription URL they can paste into Google / Outlook / Apple Calendar.
// ─────────────────────────────────────────────────────────────────────────────

type FeedEntry = {
  propertyGroupId: string;
  propertyGroupName: string;
  propertyGroupSlug: string;
  feed: { id: string; token: string; createdAt: string; lastAccessedAt: string | null } | null;
};

const CalendarSubscribePanel: React.FC = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<FeedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyGroupId, setBusyGroupId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get<FeedEntry[]>('/api/calendar/feeds');
      setEntries(res.data);
    } catch (err) {
      console.error('Failed to load calendar feeds:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async (propertyGroupId: string) => {
    setBusyGroupId(propertyGroupId);
    try {
      await api.post('/api/calendar/feeds', { propertyGroupId });
      await load();
    } catch (err) {
      console.error('Failed to generate subscription link:', err);
      alert('Could not generate subscription link. Please try again.');
    } finally {
      setBusyGroupId(null);
    }
  };

  const rotate = async (feedId: string, groupId: string) => {
    if (!window.confirm('Rotate this link? Any calendar app already subscribed with the old URL will stop syncing until you re-add the new one.')) return;
    setBusyGroupId(groupId);
    try {
      await api.post(`/api/calendar/feeds/${feedId}/rotate`);
      await load();
    } catch (err) {
      console.error('Failed to rotate feed:', err);
    } finally {
      setBusyGroupId(null);
    }
  };

  const revoke = async (feedId: string, groupId: string) => {
    if (!window.confirm('Revoke this subscription link? Anyone using it will stop receiving events.')) return;
    setBusyGroupId(groupId);
    try {
      await api.delete(`/api/calendar/feeds/${feedId}`);
      await load();
    } catch (err) {
      console.error('Failed to revoke feed:', err);
    } finally {
      setBusyGroupId(null);
    }
  };

  const buildHttpsUrl = (token: string) =>
    `${window.location.origin}/api/calendar/feed/${token}.ics`;
  const buildWebcalUrl = (token: string) =>
    `webcal://${window.location.host}/api/calendar/feed/${token}.ics`;

  const copy = async (token: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(prev => (prev === token ? null : prev)), 2000);
    } catch {
      // Fallback for older browsers
      window.prompt('Copy this URL:', url);
    }
  };

  if (loading || entries.length === 0) return null;

  return (
    <div className="floating-card">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center">
            <LinkIcon className="w-5 h-5 text-[#22d3ee]" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Subscribe in your calendar app</h3>
            <p className="text-white/60 text-sm">
              Get these events automatically in Google Calendar, Outlook, or Apple Calendar.
            </p>
          </div>
        </div>
        <span className="text-white/40 text-sm">{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded && (
        <div className="mt-6 space-y-4">
          {entries.map(entry => {
            const isBusy = busyGroupId === entry.propertyGroupId;
            return (
              <div
                key={entry.propertyGroupId}
                className="p-4 rounded-xl bg-white/5 border border-white/10"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-white font-medium">{entry.propertyGroupName}</div>
                  {entry.feed ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => rotate(entry.feed!.id, entry.propertyGroupId)}
                        disabled={isBusy}
                        className="text-xs text-white/60 hover:text-white flex items-center space-x-1 disabled:opacity-50"
                        title="Generate a new URL and invalidate the old one"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                        <span>Rotate</span>
                      </button>
                      <button
                        onClick={() => revoke(entry.feed!.id, entry.propertyGroupId)}
                        disabled={isBusy}
                        className="text-xs text-red-400 hover:text-red-300 flex items-center space-x-1 disabled:opacity-50"
                      >
                        <XMarkIcon className="w-4 h-4" />
                        <span>Revoke</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => generate(entry.propertyGroupId)}
                      disabled={isBusy}
                      className="btn-primary text-xs px-3 py-1.5"
                    >
                      {isBusy ? 'Generating…' : 'Generate link'}
                    </button>
                  )}
                </div>

                {entry.feed && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        readOnly
                        value={buildHttpsUrl(entry.feed.token)}
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                        className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs text-white/80 font-mono"
                      />
                      <button
                        onClick={() => copy(entry.feed!.token, buildHttpsUrl(entry.feed!.token))}
                        className="btn-secondary text-xs px-3 py-2 flex items-center space-x-1"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                        <span>{copiedToken === entry.feed.token ? 'Copied!' : 'Copy URL'}</span>
                      </button>
                      <a
                        href={buildWebcalUrl(entry.feed.token)}
                        className="btn-secondary text-xs px-3 py-2"
                        title="Open directly in your default calendar app"
                      >
                        Open in calendar
                      </a>
                    </div>
                    <p className="text-xs text-white/50 leading-relaxed">
                      <strong className="text-white/70">Google Calendar:</strong> Other calendars → + → From URL.{' '}
                      <strong className="text-white/70">Outlook:</strong> Add calendar → Subscribe from web.{' '}
                      <strong className="text-white/70">Apple Calendar:</strong> File → New Calendar Subscription.
                      Updates sync automatically (typically every few hours, controlled by your calendar app). This is a one-way feed — events you create in your calendar app won't sync back.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
